// apps-resize.js - Layout recalculation with intelligent cursor preservation

import state from './apps-state.js';
import * as render from './apps-render.js';
import * as folder from './apps-folders.js';

/**
 * Recalculate layout and restore cursor intelligently
 * Used for both window resize and icon size changes
 */
export function recalculateLayoutWithCursorPreservation() {
    return new Promise((resolve) => {
        import('./apps-kb-state.js').then(kbState => {
            import('./apps-kb-helpers.js').then(helpers => {
                const keyboardState = kbState.keyboardState;
                let cursorRestoreData = null;
                
                if (keyboardState.virtualCursor.active) {
                    const rank = keyboardState.virtualCursor.rank;
                    const file = keyboardState.virtualCursor.file;
                    const layout = render.calculateLayout();
                    const isFolder = !!state.openFolder;
                    const icons = helpers.getCurrentIcons();
                    const currentScreen = helpers.getCurrentScreen();
                    
                    cursorRestoreData = determineCursorRestoreStrategy(
                        rank, file, layout, isFolder, icons, currentScreen, helpers
                    );
                }
                
                // Re-render
                if (state.openFolder) {
                    const currentFolder = state.openFolder;
                    render.renderLaunchpad();
                    folder.openFolderView(currentFolder);
                } else {
                    render.renderLaunchpad();
                }
                
                // Restore cursor with new layout
                if (cursorRestoreData) {
                    import('./apps-kb-cursor.js').then(cursor => {
                        setTimeout(() => {
                            restoreCursorAfterResize(cursorRestoreData, cursor, render, helpers);
                            resolve();
                        }, 50);
                    });
                } else {
                    resolve();
                }
            });
        });
    });
}

export function initResizeHandler() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            recalculateLayoutWithCursorPreservation();
        }, 250);
    });
}

export function determineCursorRestoreStrategy(rank, file, layout, isFolder, icons, currentScreen, helpers) {
    // Special zones - restore as-is
    if (rank === -1 || rank === -0.5 || rank > layout.bands * 3 - 1) {
        return { type: 'special', rank, file };
    }
    
    if (file === -1 || file >= layout.leaves * 4) {
        return { type: 'scroll', rank, file };
    }
    
    const maxFile = layout.leaves * 4 - 1;
    
    // Integer position - could be icon or empty slot
    if (Number.isInteger(file) && file >= 0 && file <= maxFile) {
        const slotInfo = helpers.rankFileToRegionPosition(rank, file, layout, isFolder);
        if (!slotInfo) return { type: 'rankfile', rank, file };
        
        const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
        const screenStartRegion = currentScreen * regionsPerScreen;
        const actualRegion = screenStartRegion + slotInfo.region;
        
        // Check if there's an icon here
        const icon = icons.find(i => i.region === actualRegion && i.position === slotInfo.position);
        
        if (icon) {
            return { type: 'icon', iconId: icon.id };
        } else {
            return { type: 'regionpos', region: actualRegion, position: slotInfo.position };
        }
    }
    
    // Insert before first icon (file -0.5)
    if (file === -0.5) {
        const slotInfo = helpers.rankFileToRegionPosition(rank, 0, layout, isFolder);
        if (!slotInfo) return { type: 'rankfile', rank, file };
        
        const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
        const screenStartRegion = currentScreen * regionsPerScreen;
        const actualRegion = screenStartRegion + slotInfo.region;
        const refIcon = icons.find(i => i.region === actualRegion && i.position === slotInfo.position);
        
        if (refIcon) {
            return { type: 'insertbefore', iconId: refIcon.id };
        }
        return { type: 'rankfile', rank, file };
    }
    
    // Append after last icon (file MAX+0.5)
    if (file === maxFile + 0.5) {
        const slotInfo = helpers.rankFileToRegionPosition(rank, maxFile, layout, isFolder);
        if (!slotInfo) return { type: 'rankfile', rank, file };
        
        const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
        const screenStartRegion = currentScreen * regionsPerScreen;
        const actualRegion = screenStartRegion + slotInfo.region;
        const refIcon = icons.find(i => i.region === actualRegion && i.position === slotInfo.position);
        
        if (refIcon) {
            return { type: 'insertafter', iconId: refIcon.id };
        }
        return { type: 'rankfile', rank, file };
    }
    
    // Insert between (X.5)
    if (!Number.isInteger(file) && file > 0 && file < maxFile + 0.5) {
        const leftFile = Math.floor(file);
        const rightFile = Math.ceil(file);
        
        // Check if this is a region boundary
        const isRegionBoundary = (leftFile % 4 === 3) && (rightFile % 4 === 0);
        
        if (isRegionBoundary) {
            // Region boundary - use rank/file for visual gap
            return { type: 'rankfile', rank, file };
        } else {
            // Within region - track both icons
            const leftSlotInfo = helpers.rankFileToRegionPosition(rank, leftFile, layout, isFolder);
            const rightSlotInfo = helpers.rankFileToRegionPosition(rank, rightFile, layout, isFolder);
            
            if (leftSlotInfo && rightSlotInfo) {
                const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
                const screenStartRegion = currentScreen * regionsPerScreen;
                
                const leftIcon = icons.find(i => 
                    i.region === screenStartRegion + leftSlotInfo.region && 
                    i.position === leftSlotInfo.position
                );
                const rightIcon = icons.find(i => 
                    i.region === screenStartRegion + rightSlotInfo.region && 
                    i.position === rightSlotInfo.position
                );
                
                if (leftIcon && rightIcon) {
                    return { type: 'insertbetween', leftIconId: leftIcon.id, rightIconId: rightIcon.id };
                }
            }
            
            // Fallback to region/position
            if (leftSlotInfo) {
                const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
                const screenStartRegion = currentScreen * regionsPerScreen;
                return { type: 'regionpos', region: screenStartRegion + leftSlotInfo.region, position: leftSlotInfo.position };
            }
        }
    }
    
    // Default fallback
    return { type: 'rankfile', rank, file };
}

// Export a new function to restore cursor from saved strategy
export function restoreCursorFromStrategy(restoreData) {
    import('./apps-kb-cursor.js').then(cursor => {
        import('./apps-kb-helpers.js').then(helpers => {
            setTimeout(() => {
                restoreCursorAfterResize(restoreData, cursor, render, helpers);
            }, 50);
        });
    });
}

function restoreCursorAfterResize(restoreData, cursor, render, helpers) {
    const layout = render.calculateLayout();
    const isFolder = !!state.openFolder;
    const icons = helpers.getCurrentIcons();
    
    switch (restoreData.type) {
        case 'special':
        case 'scroll':
            cursor.showVirtualCursor(restoreData.rank, restoreData.file);
            break;
            
        case 'icon': {
            const icon = icons.find(i => i.id === restoreData.iconId);
            if (icon) {
                const visualPos = render.calculateVisualPosition(icon.region, icon.position, layout);
                cursor.showVirtualCursor(visualPos.rank, visualPos.file);
            }
            break;
        }
        
        case 'regionpos': {
            const visualPos = render.calculateVisualPosition(restoreData.region, restoreData.position, layout);
            cursor.showVirtualCursor(visualPos.rank, visualPos.file);
            break;
        }
        
        case 'insertbefore': {
            const icon = icons.find(i => i.id === restoreData.iconId);
            if (icon) {
                const visualPos = render.calculateVisualPosition(icon.region, icon.position, layout);
                cursor.showVirtualCursor(visualPos.rank, visualPos.file - 0.5);
            }
            break;
        }
        
        case 'insertafter': {
            const icon = icons.find(i => i.id === restoreData.iconId);
            if (icon) {
                const visualPos = render.calculateVisualPosition(icon.region, icon.position, layout);
                cursor.showVirtualCursor(visualPos.rank, visualPos.file + 0.5);
            }
            break;
        }
        
        case 'insertbetween': {
            const leftIcon = icons.find(i => i.id === restoreData.leftIconId);
            const rightIcon = icons.find(i => i.id === restoreData.rightIconId);
            
            if (leftIcon && rightIcon) {
                const leftPos = render.calculateVisualPosition(leftIcon.region, leftIcon.position, layout);
                const rightPos = render.calculateVisualPosition(rightIcon.region, rightIcon.position, layout);
                
                // Check if still adjacent
                if (leftPos.rank === rightPos.rank && rightPos.file === leftPos.file + 1) {
                    cursor.showVirtualCursor(leftPos.rank, leftPos.file + 0.5);
                } else if (leftIcon) {
                    // Not adjacent anymore, position after left icon
                    cursor.showVirtualCursor(leftPos.rank, leftPos.file);
                }
            }
            break;
        }
        
        case 'rankfile':
        default:
            cursor.showVirtualCursor(restoreData.rank, restoreData.file);
            break;
    }
}
